import { useQuery } from "@tanstack/react-query";
import {
  getCoachPlayerDevelopment,
  getMyPlayerDevelopment,
} from "@/lib/api";
import { useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";
import type { DevelopmentProfile } from "@/lib/types";

/**
 * Player Development profile hooks (M7) — one shape, two surfaces.
 *
 * `useCoachPlayerDevelopment` — the coach modal opened from
 *   Roster. Scope-keyed + gated on `teamId` like the other coach
 *   queries. The coach payload carries the coach-only `themes` +
 *   `linked_accounts`.
 *
 * `useMyPlayerDevelopment` — the viewer Development sub-tab. Scope-keyed
 *   for the same reason as the `/feedback` bundle: the API response is
 *   resolved through the active team/season request scope. The verb collapses a 404
 *   (unknown OR unrelated player) to `null`, so the caller treats
 *   `null` as "profile not available" without distinguishing the
 *   two — that's the anti-probe contract.
 *
 * Both are gated on `playerId` truthiness so they don't fire with
 * an empty id mid-render (the viewer tab resolves its active
 * player before mounting the profile).
 */

export function useCoachPlayerDevelopment(playerId: string | null) {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery<DevelopmentProfile>({
    queryKey: useScopedKey("coach-player-development", {
      playerId: playerId ?? "",
    }),
    queryFn: () => getCoachPlayerDevelopment(playerId as string),
    enabled: !!teamId && !!playerId,
  });
}

export function useMyPlayerDevelopment(playerId: string | null) {
  const queryKey = useScopedKey("my-player-development", {
    playerId: playerId ?? "",
  });
  return useQuery<DevelopmentProfile | null>({
    queryKey,
    queryFn: () => getMyPlayerDevelopment(playerId as string),
    enabled: !!playerId,
  });
}
